import React from 'react';
import { Box, Card, CardContent, Chip, Grid, Typography } from '@mui/material';

type GridSize = {
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
};

type Props = {
  title: string;
  children: React.ReactNode;
  /** Grid 幅（未指定時: モバイル1列 / md以上2列） */
  size?: GridSize;
  /** 緑アクセントで必須セクションを強調 */
  highlight?: boolean;
  /** 患者情報など表示専用画面向けの詰めレイアウト */
  compact?: boolean;
  /** Grid 外（患者情報ヘッダーなど）に単体で置く */
  standalone?: boolean;
};

const defaultSize = { xs: 12, md: 6 } as const;

/** 緑の左罫アクセント付き見出し */
const SectionHeader: React.FC<{ title: string; highlight?: boolean; compact?: boolean }> = ({
  title,
  highlight,
  compact,
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: compact ? 0.75 : 1,
      px: compact ? 1.25 : 2,
      py: compact ? 0.625 : 1,
      borderBottom: 1,
      borderColor: 'divider',
      borderLeft: compact ? 2 : 3,
      borderLeftColor: highlight ? 'primary.main' : 'transparent',
      bgcolor: highlight ? 'rgba(9,183,61,0.06)' : 'grey.50',
    }}
  >
    <Typography variant={compact ? 'subtitle2' : 'subtitle1'}>{title}</Typography>
    {highlight && (
      <Chip label="必須" size="small" color="primary" variant="outlined" sx={{ height: 20 }} />
    )}
  </Box>
);

const bodySx = (compact?: boolean) =>
  ({
    flex: 1,
    pt: compact ? 0.625 : 1,
    pb: compact ? 0.625 : 1,
    px: compact ? 1.25 : 2,
    '&:last-child': { pb: compact ? 0.625 : 1 },
    bgcolor: 'background.paper',
    '& .MuiTextField-root': { mt: 0.25, mb: 0.25 },
    '& .MuiInputLabel-root': { bgcolor: 'background.paper' },
  }) as const;

const cardSx = (highlight?: boolean, compact?: boolean) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column' as const,
  borderRadius: compact ? 1.5 : 2,
  borderColor: highlight ? 'primary.main' : 'divider',
  ...(highlight && { borderWidth: 1.5 }),
});

const IntakeSectionCardBody: React.FC<{
  title: string;
  children: React.ReactNode;
  highlight?: boolean;
  compact?: boolean;
}> = ({ title, children, highlight, compact }) => (
  <Card variant="outlined" sx={cardSx(highlight, compact)}>
    <SectionHeader title={title} highlight={highlight} compact={compact} />
    <CardContent sx={bodySx(compact)}>{children}</CardContent>
  </Card>
);

const IntakeSectionCard: React.FC<Props> = ({ title, children, size, highlight, compact, standalone }) => {
  if (standalone) {
    return (
      <IntakeSectionCardBody title={title} highlight={highlight} compact={compact}>
        {children}
      </IntakeSectionCardBody>
    );
  }

  const { xs, sm, md, lg } = { ...defaultSize, ...size };

  return (
    <Grid item xs={xs} sm={sm} md={md} lg={lg}>
      <IntakeSectionCardBody title={title} highlight={highlight} compact={compact}>
        {children}
      </IntakeSectionCardBody>
    </Grid>
  );
};

export default IntakeSectionCard;
